import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Plus, Database } from 'lucide-react';
import { Dataset } from '../../../types';
import { getTableTypeIcon } from '../../../utils/tableUtils';
import { groupByTime, formatRelativeTime } from '../../../utils/dateUtils';

interface MobileSearchModalProps {
  isOpen: boolean;
  datasets: Dataset[];
  onClose: () => void;
  onCloseDrawer: () => void;
}

interface DatasetGroupProps {
  title: string;
  datasets: Dataset[];
  onSelect: (id: string) => void;
}

const DatasetGroup: React.FC<DatasetGroupProps> = ({ title, datasets, onSelect }) => {
  if (datasets.length === 0) return null;

  return (
    <div className="mb-2">
      <div className="px-3 py-2">
        <span className="text-2xs text-neutral-400 uppercase tracking-wide font-medium">{title}</span>
      </div>
      {datasets.map((dataset) => {
        const TypeIcon = getTableTypeIcon(dataset.table_type);
        return (
          <button
            key={dataset.id}
            onClick={() => onSelect(dataset.id)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-neutral-50 active:bg-neutral-100 transition"
          >
            <div className="w-8 h-8 rounded-lg bg-neutral-100 flex items-center justify-center">
              <TypeIcon className="w-4 h-4 text-neutral-500" />
            </div>
            <span className="flex-1 text-sm text-neutral-700 truncate text-left">{dataset.name}</span>
            <span className="text-xs text-neutral-400">{formatRelativeTime(dataset.updated_at)}</span>
          </button>
        );
      })}
    </div>
  );
};

export const MobileSearchModal: React.FC<MobileSearchModalProps> = ({
  isOpen,
  datasets,
  onClose,
  onCloseDrawer,
}) => {
  const navigate = useNavigate();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = React.useState('');

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const filteredDatasets = datasets.filter(d =>
    d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedDatasets = groupByTime(filteredDatasets);

  const handleSelect = (id: string) => {
    navigate(`/datasets/${id}`);
    onClose();
    onCloseDrawer();
  };

  const handleNewTask = () => {
    navigate('/datasets');
    onClose();
    onCloseDrawer();
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-[60]"
        onClick={onClose}
      />

      <div
        className="fixed inset-x-4 top-[10vh] z-[61] bg-white rounded-2xl shadow-2xl overflow-hidden"
        style={{
          animation: 'mobileSearchSlideIn 200ms ease-out',
          maxHeight: '70vh',
        }}
      >
        <style>{`
          @keyframes mobileSearchSlideIn {
            from { opacity: 0; transform: scale(0.95) translateY(-20px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
        `}</style>

        <div className="px-4 py-3 border-b border-neutral-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search datasets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-10 py-3 text-base bg-neutral-50 border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-300 focus:bg-white transition"
            />
            <button
              onClick={() => searchQuery ? setSearchQuery('') : onClose()}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-neutral-400 hover:text-neutral-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          <div className="px-3 py-2">
            <button
              onClick={handleNewTask}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-neutral-50 active:bg-neutral-100 transition"
            >
              <div className="w-10 h-10 rounded-xl bg-neutral-900 flex items-center justify-center">
                <Plus className="w-5 h-5 text-white" />
              </div>
              <span className="text-[15px] font-medium text-neutral-900">New Task</span>
            </button>
          </div>

          <div className="h-px bg-neutral-100 mx-4" />

          <div className="px-3 py-2">
            {filteredDatasets.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Database className="w-10 h-10 text-neutral-300 mb-2" />
                <p className="text-sm text-neutral-400">
                  {searchQuery ? 'No matches found' : 'No recent datasets'}
                </p>
              </div>
            ) : (
              <>
                <DatasetGroup title="Today" datasets={groupedDatasets.today} onSelect={handleSelect} />
                <DatasetGroup title="Yesterday" datasets={groupedDatasets.yesterday} onSelect={handleSelect} />
                <DatasetGroup title="This Week" datasets={groupedDatasets.thisWeek} onSelect={handleSelect} />
                <DatasetGroup title="Older" datasets={groupedDatasets.older} onSelect={handleSelect} />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
